import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, PackagePlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "@/lib/api";
import { todayWIB } from "@/components/staff/attendance/utils";

const sel = "px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm";

const BundleCard = ({ b, idea, idx, onDrafted }) => {
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/promo-ideas/${idea.id}/bundles/${idx}/draft`);
      toast.success("Draf paket dibuat. Setujui di tab Menu agar tampil ke pelanggan.");
      onDrafted();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-paper p-5 flex flex-col" data-testid={`promo-bundle-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-semibold text-lg leading-tight" data-testid={`promo-bundle-name-${idx}`}>{b.name}</h4>
        <span className="text-xs font-bold px-2 py-1 rounded-full bg-forest text-white whitespace-nowrap">-{String(b.discount_pct).replace(".", ",")}%</span>
      </div>
      {b.tagline && <p className="text-sm italic text-muted2 mt-1">"{b.tagline}"</p>}
      <ul className="text-sm mt-3 space-y-1">
        {b.items.map((it) => <li key={it.menu_id} className="flex justify-between gap-2"><span>{it.qty}x {it.name}</span><span className="text-muted2">{rupiah(it.price * it.qty)}</span></li>)}
      </ul>
      <div className="flex items-baseline gap-2 mt-3 pt-3 border-t border-sage-200">
        <span className="text-muted2 line-through text-sm">{rupiah(b.normal_price)}</span>
        <span className="font-bold text-xl text-forest" data-testid={`promo-bundle-price-${idx}`}>{rupiah(b.promo_price)}</span>
      </div>
      {b.reason && <p className="text-xs text-muted2 mt-2 flex-1"><b className="text-ink">Alasan:</b> {b.reason}</p>}
      {b.draft_menu_id ? (
        <div className="mt-4 text-xs font-semibold text-emerald-700 flex items-center gap-1.5" data-testid={`promo-bundle-drafted-${idx}`}><Check className="w-4 h-4" /> Draf dibuat, menunggu persetujuan di tab Menu</div>
      ) : (
        <button onClick={draft} disabled={busy} className="mt-4 px-4 py-2 rounded-full bg-sage-100 font-semibold text-sm flex items-center justify-center gap-2" data-testid={`promo-bundle-draft-button-${idx}`}>
          <PackagePlus className="w-4 h-4" />{busy ? "Membuat..." : "Jadikan draf menu paket"}
        </button>
      )}
    </div>
  );
};

export const PromoIdeas = ({ outlets }) => {
  const [month, setMonth] = useState(todayWIB().slice(0, 7));
  const [outlet, setOutlet] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const code = outlet || outlets[0]?.code || "";

  const load = useCallback(() => {
    if (!code) return;
    api.get("/admin/promo-ideas", { params: { month, outlet: code } }).then((r) => setIdeas(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month, code]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setLoading(true);
    try { await api.post("/admin/promo-ideas/generate", null, { params: { month, outlet: code } }); toast.success("3 ide promo baru siap"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const idea = ideas[0];

  return (
    <div data-testid="promo-ideas">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif font-semibold text-2xl flex items-center gap-2"><Sparkles className="w-6 h-6" /> Ide promo paket</h2>
          <p className="text-sm text-muted2">AI memadukan menu terlaris dengan menu jarang laku. Harga paket otomatis sekitar 20% lebih hemat.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={sel} data-testid="promo-month-input" />
          <select value={code} onChange={(e) => setOutlet(e.target.value)} className={sel} data-testid="promo-outlet-select">
            {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
          </select>
          <button onClick={generate} disabled={loading || !code} className="btn-forest px-4 py-2 text-sm flex items-center gap-2" data-testid="promo-generate-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{loading ? "AI sedang menyusun..." : idea ? "Buat ide baru" : "Buat ide promo"}
          </button>
        </div>
      </div>
      {!idea && !loading && <p className="text-center text-muted2 py-12" data-testid="promo-empty">Belum ada ide promo untuk gerai & bulan ini. Klik "Buat ide promo".</p>}
      {idea && (
        <>
          <p className="text-xs text-muted2 mt-4" data-testid="promo-generated-at">Dibuat {new Date(idea.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB · {idea.outlet_code} · {idea.month}</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
            {idea.bundles.map((b, i) => <BundleCard key={i} b={b} idea={idea} idx={i} onDrafted={load} />)}
          </div>
        </>
      )}
    </div>
  );
};
